import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import { cn } from 'cn';
import { type ComponentProps, forwardRef, type ReactNode, useId } from 'react';

import { isFieldRequired, unique } from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly flex?: '1' | 'auto' | 'initial' | 'none';
  readonly hint?: string;
  readonly icon?: ReactNode;
  readonly unit?: string;
  readonly isDisabled?: boolean;
} & ComponentProps<typeof MuiTextField>;

export const NumberField = forwardRef<HTMLInputElement, P>(
  (
    {
      autoComplete = 'off',
      className,
      flex = '1',
      hint,
      isDisabled = false,
      label,
      placeholder,
      style,
      unit,
      ...rest
    },
    ref,
  ) => {
    const field = useFieldContext<string>();

    const isRequired = isFieldRequired(
      field.options.form.options.validators,
      field.name,
    );

    const hasErrors = Boolean(field.state.meta.errors?.length);

    const id = useId();

    return (
      <div className={cn('relative', className)}>
        {Boolean(label) && (
          <InputLabel
            className="text-right"
            hint={hint}
            htmlFor={id}
            isRequired={isRequired}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={cn(
            'flex items-center gap-x-2 overflow-hidden transition-colors duration-200 ease-out',
            {
              'flex-auto': flex === 'auto',
              'flex-initial': flex === 'initial',
              'flex-none': flex === 'none',
            },
          )}
          style={style}
        >
          <MuiTextField
            autoComplete={autoComplete}
            className="w-full flex-1 focus:outline-none [&_input]:text-right"
            disabled={isDisabled}
            error={hasErrors}
            id={id}
            inputRef={ref}
            placeholder={placeholder}
            size="small"
            slotProps={{
              input: {
                ...(unit
                  ? {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          className="[&>p]:text-tiga-text-lighter"
                        >
                          {unit}
                        </InputAdornment>
                      ),
                    }
                  : undefined),
                className: 'bg-white',
              },
            }}
            type="number"
            value={field.state.value ?? ''}
            onBlur={field.handleBlur}
            onChange={(event) => {
              field.handleChange(event.target.value);
            }}
            {...rest}
          />
        </div>
        {hasErrors ? (
          <ValidationError>
            {unique(field.state.meta.errors.map(({ message }) => message)).join(
              ', ',
            )}
          </ValidationError>
        ) : null}
      </div>
    );
  },
);

NumberField.displayName = 'NumberField';
